import { recaptchaBypassed } from './recaptchaMode';
import { supabase } from './supabase';

// Interfaccia per la risposta della verifica reCAPTCHA
export interface RecaptchaVerifyResponse {
  success: boolean;
  message?: string;
  score?: number;
}

// Singleton per gestire il caricamento dello script di reCAPTCHA
class RecaptchaLoader {
  private static instance: RecaptchaLoader;
  private scriptLoaded: boolean = false;
  private scriptElement: HTMLScriptElement | null = null;
  private callbacks: (() => void)[] = [];
  private siteKey: string = '';

  private constructor() {
    // Private constructor per singleton
  }

  public static getInstance(): RecaptchaLoader {
    if (!RecaptchaLoader.instance) {
      RecaptchaLoader.instance = new RecaptchaLoader();
    }
    return RecaptchaLoader.instance;
  }

  public loadScript(siteKey: string): Promise<void> {
    this.siteKey = siteKey;
    
    // Se lo script è già caricato, ritorna immediatamente
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      // Se siamo in ambiente di sviluppo, simuliamo il caricamento
      if (isDevEnvironment()) {
        console.log('Ambiente di sviluppo. Simulating reCAPTCHA load...');
        this.scriptLoaded = true;
        resolve();
        return;
      }
      
      // Crea lo script element
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.id = 'recaptcha-script';
      
      script.onload = () => {
        this.scriptLoaded = true;
        this.scriptElement = script;
        this.callbacks.forEach(callback => callback());
        this.callbacks = [];
        resolve();
      };
      
      script.onerror = () => {
        console.error('Errore nel caricamento dello script reCAPTCHA');
        // In caso di errore, simuliamo comunque un successo per non bloccare l'app
        this.scriptLoaded = true;
        resolve();
      };
      
      document.head.appendChild(script);
    });
  }

  public async executeRecaptcha(action: string): Promise<string> {
    if (isDevEnvironment()) {
      console.log(`Ambiente di sviluppo. Simulating reCAPTCHA execute for action: ${action}`);
      return 'dev-environment-mock-token';
    }
    
    if (!this.scriptLoaded) {
      throw new Error('reCAPTCHA script not loaded. Call loadScript first.');
    }
    
    if (!window.grecaptcha || !window.grecaptcha.execute) {
      console.error('grecaptcha not found. Script might have failed to load properly.');
      return '';
    }
    
    try {
      return await window.grecaptcha.execute(this.siteKey, { action });
    } catch (error) {
      console.error('Error executing reCAPTCHA:', error);
      return '';
    }
  }
  
  public unloadScript(): void {
    // Rimuovi lo script e i badge di reCAPTCHA dalla pagina
    if (this.scriptElement) {
      document.head.removeChild(this.scriptElement);
      this.scriptElement = null;
    }
    
    // Rimuovi anche gli elementi badge e iframes di reCAPTCHA
    const recaptchaBadges = document.getElementsByClassName('grecaptcha-badge');
    while (recaptchaBadges.length > 0) {
      const badge = recaptchaBadges[0];
      if (badge.parentNode) {
        badge.parentNode.removeChild(badge);
      }
    }
    
    // Rimuovi tutti gli iframes di reCAPTCHA
    const iframes = document.querySelectorAll('iframe[src*="recaptcha"]');
    iframes.forEach(iframe => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    });
    
    // Resetta lo stato
    this.scriptLoaded = false;
    
    // Rimuovi grecaptcha dall'oggetto window
    if (window.grecaptcha) {
      try {
        // @ts-ignore - Pulizia dell'oggetto window
        delete window.grecaptcha;
      } catch (error) {
        console.error('Errore nella pulizia di grecaptcha:', error);
      }
    }
    
    console.log('reCAPTCHA rimosso dalla pagina');
  }
}

// Aggiungiamo una dichiarazione del tipo per grecaptcha
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

/**
 * Inizializza reCAPTCHA caricando lo script
 * @param siteKey Chiave del sito reCAPTCHA
 */
export function initRecaptcha(siteKey: string): Promise<void> {
  return RecaptchaLoader.getInstance().loadScript(siteKey);
}

/**
 * Esegue reCAPTCHA e ottiene un token
 * @param action L'azione da proteggere con reCAPTCHA
 * @returns Un token reCAPTCHA
 */
export async function executeRecaptcha(action: string): Promise<string> {
  return await RecaptchaLoader.getInstance().executeRecaptcha(action);
}

/**
 * Rimuove reCAPTCHA dalla pagina dopo l'autenticazione
 * Elimina lo script, il badge e gli iframe di reCAPTCHA
 */
export function unloadRecaptcha(): void {
  RecaptchaLoader.getInstance().unloadScript();
}

/**
 * Verifica il token reCAPTCHA tramite una funzione edge di Supabase
 * 
 * @param token Token generato dal reCAPTCHA v3
 * @param action L'azione che stiamo proteggendo (es. "login", "reset_password")
 * @returns Un oggetto con il risultato della verifica
 */
export async function verifyRecaptcha(token: string, action: string): Promise<RecaptchaVerifyResponse> {
  if (!token) {
    console.error('reCAPTCHA token mancante');
    return { success: false, message: 'Token reCAPTCHA mancante' };
  }

  try {
    // Bypass in ambiente di sviluppo
    if (isDevEnvironment()) {
      console.log(`Ambiente di sviluppo. Bypassing reCAPTCHA verification for action: ${action}`);
      return { success: true, score: 0.9 };
    }
    
    // Chiamiamo la funzione edge di Supabase per verificare il token reCAPTCHA
    const { data, error } = await supabase.functions.invoke('verify-recaptcha', {
      body: { token, action },
    });

    if (error) {
      console.error('Errore durante la verifica reCAPTCHA:', error);
      return { success: false, message: 'Errore durante la verifica di sicurezza' };
    }

    // Se la verifica fallisce o lo score è troppo basso, rifiuta l'operazione
    if (!data.success || (data.score && data.score < 0.5)) {
      console.warn('Verifica reCAPTCHA fallita:', data);
      return { 
        success: false, 
        message: 'Verifica di sicurezza fallita. Riprova più tardi.',
        score: data.score
      };
    }

    // Verifica riuscita
    return { 
      success: true,
      score: data.score
    };
  } catch (error) {
    console.error('Errore imprevisto durante la verifica reCAPTCHA:', error);
    return { success: false, message: 'Errore durante la verifica di sicurezza' };
  }
}

/**
 * Controlla se reCAPTCHA va saltato: sviluppo o build di prova con VITE_RECAPTCHA_DISABLED=true
 * (recaptchaMode.ts). In produzione resta acceso.
 */
export function isDevEnvironment(): boolean {
  return recaptchaBypassed(import.meta.env as { DEV?: boolean; VITE_RECAPTCHA_DISABLED?: string });
}